import ky, { HTTPError, KyInstance, Options, ResponsePromise, TimeoutError } from 'ky'
import { camelCase, snakeCase } from 'change-case'
// HttpMethod is an internal type in ky and is not re-exported from its main entry point.
// moduleResolution: bundler (enforced by Next.js 16.2.3) disallows deep imports like
// 'ky/distribution/types/options', so we define it locally instead.
type HttpMethod =
  | 'get'
  | 'post'
  | 'put'
  | 'patch'
  | 'head'
  | 'delete'
  | 'options'
  | 'trace'
  | string
import { get, has, isEmpty, isObject, omitBy, once, trimStart } from 'lodash-es'
import template from 'lodash-es/template'
import useSWR, { Fetcher, preload, SWRConfiguration, SWRResponse } from 'swr'
import isString from 'lodash-es/isString'
import fastDeepEqual from 'fast-deep-equal'
import * as Sentry from '@sentry/nextjs'
import useSWRMutation, { SWRMutationConfiguration, SWRMutationResponse } from 'swr/mutation'

import { ListFilters, SortDirection } from '@/lib/core/interfaces/common.types'

import { caseConverter } from '@/lib/utils/case-converter'

// Import auth store for synchronization - using dynamic import to avoid circular dependency
const getAuthStore = once(async () => {
  return (await import('@/feature/auth/store/auth')).useAuthStore
})

export const BACKEND_URL =
  process.env.NEXT_PUBLIC_BACKEND_URL &&
  `${process.env.NEXT_PUBLIC_BACKEND_URL}/`.replace(/\/\/$/, '/')

const LOGIN_URL = '/v1/auth/jwt/create/'
const REFRESH_URL = '/v1/auth/jwt/refresh/'
const VERIFY_URL = '/v1/auth/jwt/verify/'

export type LoginResponse = {
  access: string
  refresh: string
}

const sortingConverter = ({ ordering, ...params }: ListFilters) => {
  return {
    ...params,
    ...(!isEmpty(ordering)
      ? {
          ordering: isObject(ordering)
            ? Object.entries(ordering)
                .filter(([, v]) => v)
                .map(([key, direction]) => (direction === SortDirection.DESC ? `-${key}` : key))
                .join(',')
            : ordering,
        }
      : {}),
  }
}

export const getAuthHeader = (token: string) => `JWT ${token}`

export class RequestWrapper {
  private refreshPromise: Promise<string | null> | null = null
  private refreshSubscribers: ((token: string | null) => void)[] = []
  private notifySubscribers(newToken: string | null) {
    this.refreshSubscribers.forEach((callback) => callback(newToken))
    this.refreshSubscribers = []
  }
  private authToken: string | null = null
  private refreshToken: string | null = null
  private readonly defaults = {
    prefixUrl: BACKEND_URL,
    hooks: {
      beforeRequest: [
        async (request, options) => {
          const { params } = options as unknown as { params: Record<string, string> }
          if (params) {
            const url = new URL(request.url)
            url.search = new URLSearchParams(
              caseConverter(
                sortingConverter(omitBy(params, (v) => v == null)),
                snakeCase,
              ) as Record<string, string>,
            ).toString()
            request = new Request(url.toString(), request)
          }

          if (this.isAuthorizationTokenSet() && !request.headers.has('Authorization')) {
            const headers = request.headers
            headers.set('Authorization', this.getAuthorizationToken()!)

            request = new Request(request, { headers })
          }
          // Ignore convert in case use FormData
          if (options.body && typeof options.body === 'string') {
            request = new Request(request, {
              body: JSON.stringify(caseConverter(await request.json(), snakeCase)),
            })
          }

          const url = new URL(request.url)

          if (url.searchParams.size > 0) {
            const params = Object.fromEntries(url.searchParams.entries())
            url.search = new URLSearchParams(
              caseConverter(sortingConverter(params), snakeCase) as Record<string, string>,
            ).toString()

            request = new Request(url.toString(), request)
          }

          return request
        },
      ],
      afterResponse: [
        async (request, options, response) => {
          if (response.body) {
            if (response.ok) {
              try {
                // We have to clone the response because it's a stream and we can't read it twice
                const data = await (response.clone() as typeof response).json<
                  Record<string, string>
                >()

                return new Response(JSON.stringify(caseConverter(data, camelCase)), response)
              } catch {
                return response
              }
            } else if (response.status === 401 && this.isAuthorizationTokenSet()) {
              if (this.isRefreshing()) {
                return new Promise((resolve, reject) => {
                  this.refreshSubscribers.push((newToken) => {
                    if (newToken) {
                      request.headers.set('Authorization', newToken)
                      resolve(this.instance(request, options))
                    } else {
                      reject(new HTTPError(response, request, options))
                    }
                  })
                })
              } else {
                this.startRefresh()
                this.refreshPromise = this.refreshTokens()
                  .then(async (newAccessToken) => {
                    if (newAccessToken) {
                      const newToken = getAuthHeader(newAccessToken)
                      await this.syncTokensWithStore(newAccessToken)
                      this.notifySubscribers(newToken)
                      return newToken
                    } else {
                      await this.clearTokensAndNotifyStore()
                      this.notifySubscribers(null)
                      return null
                    }
                  })
                  .catch(async (err) => {
                    console.error('Token refresh failed:', err)
                    await this.clearTokensAndNotifyStore()
                    this.notifySubscribers(null)
                    throw err
                  })
                  .finally(() => {
                    this.finishRefresh()
                    this.refreshPromise = null
                  })

                return this.refreshPromise.then((newToken) => {
                  if (newToken) {
                    request.headers.set('Authorization', newToken)
                    return this.instance(request, options)
                  } else {
                    throw new HTTPError(response, request, options)
                  }
                })
              }
              // } else if (
              //   response.status !== 401 ||
              //   Object.values(UnrestrictedEnpoints).includes(
              //     trimStart(new URL(response.url).pathname, '/') as UnrestrictedEnpoints,
              //   )
              // ) {
              //   throw new HTTPError(
              //     new Response(JSON.stringify(this.parseError(await response.json())), response),
              //     request,
              //     options,
              //   )
            } else if (
              request.headers.has('Authorization') &&
              request.headers.get('Authorization') !== this.getAuthorizationToken()
            ) {
              request.headers.delete('Authorization')

              return this.instance(request, options)
            }
          }
        },
      ],
      beforeError: [
        async (error) => {
          if (error.response) {
            try {
              return new HTTPError(
                new Response(
                  JSON.stringify(this.parseError(await error.response.json())),
                  error.response,
                ),
                error.request,
                error.options,
              )
            } catch (parseError) {
              console.warn('Failed to parse error response:', parseError)
              return new HTTPError(
                new Response(JSON.stringify(this.parseError()), error.response),
                error.request,
                error.options,
              )
            }
          }

          return error
        },
      ],
    },
  } satisfies Options
  private readonly instance: KyInstance = ky.create(this.defaults)

  private isRefreshingTriggered = false

  setAuthorizationToken(token: string) {
    this.authToken = getAuthHeader(token)
  }

  getAuthorizationToken() {
    return this.authToken
  }

  isAuthorizationTokenSet() {
    return !!this.authToken
  }

  setRefreshToken(token: string) {
    this.refreshToken = token
  }

  getRefreshToken() {
    return this.refreshToken
  }

  removeTokens() {
    this.authToken = null
    this.refreshToken = null
  }

  setIsRefresh(status: boolean) {
    this.isRefreshingTriggered = status
  }

  startRefresh() {
    this.setIsRefresh(true)
  }

  finishRefresh() {
    this.setIsRefresh(false)
  }

  isRefreshing() {
    return this.isRefreshingTriggered
  }

  parseError(error?: Record<string, string>) {
    const convertedData = caseConverter(error ?? {}, camelCase)
    let data: unknown

    if (
      typeof convertedData === 'string' ||
      typeof convertedData === 'number' ||
      typeof convertedData === 'boolean' ||
      typeof convertedData === 'undefined'
    ) {
      data = convertedData
    } else {
      const nonFieldErorrs = error?.non_field_errors || []

      data = {
        ...convertedData,
        _error: nonFieldErorrs,
      }
    }

    return data
  }

  generateResponseError(e: unknown, url: string, method: string, data?: unknown) {
    // TypeErrors are fetch errors, so we should handle them
    if (e instanceof TypeError) {
      // This is an unusual error (e.g. backend is unreachable), so we should log it
      Sentry.captureException(e, {
        level: 'warning',
      })
      console.error(e, "Transferring to 418 error, because it's an unusual error")
      // We're handling this error, like it was a 500 error for compatibility with the old axios
      return new HTTPError(
        new Response(JSON.stringify(this.parseError()), { status: 418 }),
        new Request(new URL(url, BACKEND_URL), {
          method,
          body: data == undefined ? undefined : JSON.stringify(data),
        }),
        {
          method: 'POST',
          prefixUrl: BACKEND_URL!,
          onDownloadProgress: undefined,
          onUploadProgress: undefined,
          context: {},
          retry: {},
        },
      )
    }
    return e
  }

  async get<P, QP = unknown>(
    url: string,
    config?: Options & { params?: QP },
  ): Promise<{
    data: P
    config?: Options & { url: string; params?: QP }
    response: ResponsePromise
    url: string
  }> {
    try {
      const req = this.instance.get(trimStart(url, '/'), config)

      return { data: await req.json(), response: req, config: { url, ...config }, url }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, 'GET')
    }
  }

  async post<P>(
    url: string,
    data?: unknown,
    config?: Options,
  ): Promise<{
    data: P
    config?: Options & { url: string; data?: string }
    response: ResponsePromise
    url: string
  }> {
    try {
      const req = this.instance.post(trimStart(url, '/'), { json: data, ...config })
      return {
        data: await req.json(),
        response: req,
        config: { url, data: JSON.stringify(data), ...config },
        url,
      }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, 'POST', data)
    }
  }

  async postFormData<P>(
    url: string,
    data: FormData,
    config?: Options,
  ): Promise<{
    data: P
    config?: Options & { url: string; data?: string }
    response: ResponsePromise
    url: string
  }> {
    try {
      const req = this.instance.post(trimStart(url, '/'), { body: data, ...config })
      return {
        data: await req.json(),
        response: req,
        config: { url, data: JSON.stringify(data), ...config },
        url,
      }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, 'POST', data)
    }
  }

  async options<P>(
    url: string,
    config?: Options,
  ): Promise<{
    data: P
    config?: Options & { url: string; data?: string }
    response: ResponsePromise
    url: string
  }> {
    try {
      const req = this.instance(trimStart(url, '/'), { ...config, method: 'options' })
      return {
        data: await req.json(),
        response: req,
        config: { url, ...config },
        url,
      }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, 'OPTIONS')
    }
  }

  async patch<P>(
    url: string,
    data?: unknown,
    config?: Options,
  ): Promise<{
    data: P
    config?: Options & { url: string; data?: string }
    response: ResponsePromise
    url: string
  }> {
    try {
      const req = this.instance.patch(trimStart(url, '/'), { json: data, ...config })
      return {
        data: await req.json(),
        response: req,
        config: { url, data: JSON.stringify(data), ...config },
        url,
      }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, 'PATCH', data)
    }
  }

  async put<P>(
    url: string,
    data?: unknown,
    config?: Options,
  ): Promise<{
    data: P
    config?: Options & { url: string; data?: string }
    response: ResponsePromise
    url: string
  }> {
    try {
      const req = this.instance.put(trimStart(url, '/'), { json: data, ...config })
      return {
        data: await req.json(),
        response: req,
        config: { url, data: JSON.stringify(data), ...config },
        url,
      }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, 'PUT', data)
    }
  }

  async delete<P>(
    url: string,
    config?: Options,
  ): Promise<{
    data: P
    config?: Options & { url: string; data?: string }
    response: ResponsePromise
    url: string
  }> {
    try {
      const req = this.instance.delete(trimStart(url, '/'), config)
      return {
        data: await req.json(),
        response: req,
        config: { url, ...config },
        url,
      }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, 'DELETE')
    }
  }

  async request<Result, Payload = never>(url: string, config?: Options, data?: Payload) {
    try {
      const req = this.instance<Result>(trimStart(url, '/'), { json: data, ...config })
      return {
        data: await req.json(),
        response: req,
        config: { url, ...config },
        url,
      }
    } catch (e) {
      // Handle Fetch errors otherwise rethrow
      throw this.generateResponseError(e, url, config?.method ?? 'GET', data)
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const { data } = await this.post<LoginResponse>(LOGIN_URL, { email, password })

      this.setAuthorizationToken(data.access)
      this.setRefreshToken(data.refresh)

      return data
    } catch (e) {
      throw this.generateResponseError(e, LOGIN_URL, 'POST', { email, password })
    }
  }

  async logout(): Promise<void> {
    this.removeTokens()
  }

  async clearTokensAndNotifyStore(): Promise<void> {
    this.removeTokens()
    try {
      const useAuthStore = await getAuthStore()
      const authStore = useAuthStore.getState()
      if (authStore.isAuthenticated) {
        useAuthStore.setState({
          isAuthenticated: false,
          accessToken: null,
          refreshToken: null,
        })
      }
    } catch (error) {
      console.warn('Could not sync logout with auth store:', error)
    }
  }

  async syncTokensWithStore(newAccessToken: string): Promise<void> {
    try {
      const useAuthStore = await getAuthStore()
      const authStore = useAuthStore.getState()
      if (authStore.isAuthenticated && authStore.accessToken !== newAccessToken) {
        useAuthStore.setState({
          accessToken: newAccessToken,
        })
      }
    } catch (error) {
      console.warn('Could not sync token refresh with auth store:', error)
    }
  }

  async refreshTokens(): Promise<string | undefined> {
    const refreshToken = this.refreshToken
    if (refreshToken) {
      try {
        const {
          data: { access },
        } = await this.post<{ access: string }>(REFRESH_URL, {
          refresh: refreshToken,
        })

        this.setAuthorizationToken(access)
        return access
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        await this.clearTokensAndNotifyStore()
        return undefined
      }
    }
    return undefined
  }

  async authorize(accessToken: string, refreshToken: string): Promise<boolean> {
    try {
      const response = await this.instance.post(trimStart(VERIFY_URL, '/'), {
        json: { token: accessToken },
        throwHttpErrors: false,
      })

      if (!response.ok) {
        await this.clearTokensAndNotifyStore()
        return false
      }

      this.setAuthorizationToken(accessToken)
      this.setRefreshToken(refreshToken)

      return true
    } catch (verifyError) {
      if (verifyError instanceof TimeoutError) {
        return true
      }

      await this.clearTokensAndNotifyStore()
      return false
    }
  }
}

export const request = new RequestWrapper()

export type RequestConfig<P, QP = unknown> = Parameters<typeof request.get<P, QP>>[1]
export const networkRequestFetcher = <Data, Params>(
  param: { url: string; config?: RequestConfig<Data, Params> } | string,
) =>
  request
    .get<Data>(isString(param) ? param : param.url, isString(param) ? undefined : param.config)
    .then((res) => res?.data)

export type MutationConfig<P, QP = unknown> = Parameters<typeof request.request<P, QP>>[1]
export const networkMutationFetcher = <
  Data,
  Params,
  TriggerArg extends
    | { params: Record<string, unknown>; data: Record<string, unknown> }
    | Record<string, unknown>
    | Record<string, unknown>[] = never,
>(
  param: {
    url: string
    config?: MutationConfig<Data, Params>
    method: HttpMethod
  },
  data: { arg: TriggerArg },
) =>
  request
    .request<
      Data,
      TriggerArg extends { params: Record<string, unknown>; data: Record<string, unknown> }
        ? TriggerArg['data']
        : TriggerArg
    >(template(param.url)({ params: get(data, 'arg.params') }), { method: param.method, ...param.config }, (has(data, 'arg.data') && has(data, 'arg.params') ? get(data, 'arg.data') : data?.arg) as TriggerArg extends { params: Record<string, unknown>; data: Record<string, unknown> } ? TriggerArg['data'] : TriggerArg)
    .then((res) => res?.data)

export type SWRKeyType<T> = T | (() => T) | null | undefined | false

export type SWRRequestKey = SWRKeyType<Parameters<typeof networkRequestFetcher>[0]>

export function useNetworkRequest<
  Data,
  Error = unknown,
  SWROptions extends SWRConfiguration<Data, Error, Fetcher<Data, SWRRequestKey>> = SWRConfiguration<
    Data,
    Error,
    Fetcher<Data, SWRRequestKey>
  >,
>(
  config: SWRRequestKey,
  swrOptions?: SWROptions,
): SWRResponse<Data, Error, SWROptions & { keepPreviousData: true }> {
  return useSWR<Data>(config, networkRequestFetcher, {
    compare: fastDeepEqual,
    keepPreviousData: true,
    ...swrOptions,
  })
}

export function preloadNetworkRequest<Data>(config: SWRRequestKey) {
  return preload<Data, SWRRequestKey>(config, networkRequestFetcher)
}

type SWRMutationKey = SWRKeyType<Parameters<typeof networkMutationFetcher>[0]>
type TriggerParam = Parameters<typeof networkMutationFetcher>[1]['arg']
export function useNetworkMutation<
  Data,
  TriggerArg extends TriggerParam = never,
  Error = unknown,
  SWROptions extends
    | SWRMutationConfiguration<Data, Error, SWRMutationKey, TriggerArg, Data>
    | undefined =
    | SWRMutationConfiguration<Data, Error, SWRMutationKey, TriggerArg, Data>
    | undefined,
>(
  config: SWRMutationKey,
  swrOptions?: SWROptions,
): SWRMutationResponse<Data, Error, SWRMutationKey, TriggerArg> {
  return useSWRMutation<Data, Error, SWRMutationKey, TriggerArg>(
    config,
    networkMutationFetcher,
    swrOptions,
  )
}
